import { useState, useMemo } from "react";
import { toast } from "sonner";
import { Loader2, Ruler, Plus, Minus, DoorClosed, DoorOpen, Wand2, RotateCcw, ArrowLeft, FileDown, AlertTriangle } from "lucide-react";
import { jsPDF } from "jspdf";
import SaveDestinations from "@/components/gallery/SaveDestinations";

// ────────────────────────────────────────────────────────────────────────
// Van Flatpack Blueprint Maker — parametric TWIN-BAY cabinet carcass.
// Deterministic computational-geometry engine (no AI). Two view states:
//   • CAD  — isometric wireframe on blueprint-blue grid.
//   • IKEA — flat black line-art on white.
// Exports: PNG image + multi-page vector A4 PDF (cut list + hardware + assembly).
// ────────────────────────────────────────────────────────────────────────

const SVGW = 1200, SVGH = 1680;
const num = (v, d = 0) => (Number.isFinite(+v) ? +v : d);

const THEMES = {
  cad: { bg: "#0b2545", grid: "#123a63", frame: "#1c4e7a", line: "#dbe9ff", shelf: "#2e6ea0", accent: "#39c6ff", txt: "#eaf3ff", sub: "#9cc0e2", fill: true, iso: true },
  ikea: { bg: "#ffffff", grid: "#f0f0f0", frame: "none", line: "#000000", shelf: "none", accent: "#000000", txt: "#000000", sub: "#555555", fill: false, iso: false },
};

// ── MATH ENGINE ──────────────────────────────────────────────────────────
function validate(s) {
  const errs = [];
  for (const [k, v] of Object.entries({ W: s.W, D: s.D, H: s.H, T: s.T, L: s.L })) {
    if (!Number.isFinite(v) || v <= 0) errs.push(`${k} must be greater than 0`);
  }
  if (errs.length) return { ok: false, msg: errs[0], rightBay: 0 };
  if (s.H <= 2 * s.T) return { ok: false, msg: "Height must exceed 2× board thickness", rightBay: 0 };
  const rightBay = s.W - 2 * s.T - s.L - s.T; // = W - 3T - L
  if (s.L >= s.W - 3 * s.T) return { ok: false, msg: `Left bay too wide — max ${s.W - 3 * s.T} for these W/T`, rightBay };
  if (rightBay <= 0) return { ok: false, msg: "Right bay resolves to ≤ 0", rightBay };
  return { ok: true, msg: "", rightBay };
}

function computePanels(s) {
  const { W, D, H, T, L, unit, back } = s;
  const rightBay = W - 3 * T - L;
  const innerH = H - 2 * T;
  const shelfDepth = D - (back === "recessed" ? T : 0);
  const rows = [
    { p: "Top panel", q: 1, size: `${W} × ${D}`, note: "full span" },
    { p: "Bottom panel", q: 1, size: `${W} × ${D}`, note: "full span" },
    { p: "Outer side wall", q: 2, size: `${D} × ${innerH}`, note: "between top & bottom" },
    { p: "Centre divider", q: 1, size: `${D} × ${innerH}`, note: "twin-bay split" },
    { p: "Back panel", q: 1, size: `${W} × ${H}`, note: back === "recessed" ? "recessed" : "full square" },
  ];
  if (s.leftShelves > 0) rows.push({ p: "Shelf — left bay", q: s.leftShelves, size: `${L} × ${shelfDepth}`, note: "" });
  if (s.rightShelves > 0) rows.push({ p: "Shelf — right bay", q: s.rightShelves, size: `${rightBay} × ${shelfDepth}`, note: "" });
  if (s.doorType === "swing") {
    rows.push({ p: "Door — swing (L)", q: 1, size: `${L} × ${innerH}`, note: "hinged" });
    rows.push({ p: "Door — swing (R)", q: 1, size: `${rightBay} × ${innerH}`, note: "hinged" });
  } else if (s.doorType === "sliding") {
    rows.push({ p: "Door — sliding", q: 2, size: `${Math.round((W - 2 * T) * 0.55)} × ${innerH}`, note: "front track" });
  }
  return { rows, rightBay, innerH, shelfDepth, unit };
}

function computeHardware(s, panels) {
  const totalShelves = s.leftShelves + s.rightShelves;
  const jointCount = 6; // 2 sides + divider, each to top & bottom
  return [
    { p: "Confirmat screws 7×50", q: jointCount * 3 + totalShelves * 4 },
    { p: "Wooden dowels 8×30", q: jointCount * 2 },
    { p: "Shelf support pins", q: totalShelves * 4 },
    { p: "Back panel screws 4×16", q: Math.ceil((2 * (s.W + s.H)) / 150) },
    ...(s.doorType === "swing" ? [{ p: "Concealed hinges", q: 4 }, { p: "Door handles", q: 2 }] : []),
    ...(s.doorType === "sliding" ? [{ p: "Sliding track set", q: 1 }, { p: "Door pulls", q: 2 }] : []),
  ];
}

// ── SVG PREVIEW (theme-aware) ────────────────────────────────────────────
function arrowDim(x1, y1, x2, y2, label, color, horizontal) {
  const a = 6;
  let g = `<line x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}" stroke="${color}" stroke-width="1.5"/>`;
  if (horizontal) {
    g += `<line x1="${x1}" y1="${y1 - a}" x2="${x1}" y2="${y1 + a}" stroke="${color}" stroke-width="1.5"/>`;
    g += `<line x1="${x2}" y1="${y2 - a}" x2="${x2}" y2="${y2 + a}" stroke="${color}" stroke-width="1.5"/>`;
    const mx = (x1 + x2) / 2;
    g += `<rect x="${mx - 52}" y="${y1 - 14}" width="104" height="26" fill="${color === "#000000" ? "#ffffff" : "#0b2545"}"/>`;
    g += `<text x="${mx}" y="${y1 + 6}" fill="${color}" font-size="21" text-anchor="middle" font-family="monospace">${label}</text>`;
  } else {
    g += `<line x1="${x1 - a}" y1="${y1}" x2="${x1 + a}" y2="${y1}" stroke="${color}" stroke-width="1.5"/>`;
    g += `<line x1="${x2 - a}" y1="${y2}" x2="${x2 + a}" y2="${y2}" stroke="${color}" stroke-width="1.5"/>`;
    const my = (y1 + y2) / 2;
    g += `<text x="${x1 - 8}" y="${my}" fill="${color}" font-size="21" text-anchor="middle" font-family="monospace" transform="rotate(-90 ${x1 - 8} ${my})">${label}</text>`;
  }
  return g;
}

function frontElevation(px, py, pw, ph, s, th) {
  const { W, H, T, L, unit } = s;
  const rightBay = W - 3 * T - L;
  const sc = Math.min((pw - 190) / W, (ph - 200) / H);
  const dw = W * sc, dh = H * sc, ts = Math.max(2, T * sc);
  const ox = px + 100 + (pw - 190 - dw) / 2, oy = py + 56 + (ph - 160 - dh) / 2;
  let g = `<text x="${px + pw / 2}" y="${py + 30}" fill="${th.txt}" font-size="25" font-weight="700" text-anchor="middle" font-family="monospace">FRONT · TWIN BAY</text>`;
  const band = (x, y, w, h) => th.fill
    ? `<rect x="${x}" y="${y}" width="${w}" height="${h}" fill="${th.frame}"/>`
    : `<rect x="${x}" y="${y}" width="${w}" height="${h}" fill="none" stroke="${th.line}" stroke-width="2"/>`;
  g += `<rect x="${ox}" y="${oy}" width="${dw}" height="${dh}" fill="none" stroke="${th.line}" stroke-width="2"/>`;
  g += band(ox, oy, ts, dh);                       // left wall
  g += band(ox + dw - ts, oy, ts, dh);             // right wall
  g += band(ox, oy, dw, ts);                       // top
  g += band(ox, oy + dh - ts, dw, ts);             // bottom
  const divX = ox + (T + L) * sc;                  // centre divider
  g += band(divX, oy + ts, ts, dh - 2 * ts);
  // shelves
  const innerTop = oy + ts, innerH = dh - 2 * ts;
  const drawShelves = (x0, w, n) => {
    let o = "";
    for (let i = 1; i <= n; i++) {
      const y = innerTop + (innerH * i) / (n + 1);
      o += th.fill
        ? `<rect x="${x0}" y="${y - ts / 2}" width="${w}" height="${ts}" fill="${th.shelf}"/>`
        : `<line x1="${x0}" y1="${y}" x2="${x0 + w}" y2="${y}" stroke="${th.line}" stroke-width="2"/>`;
    }
    return o;
  };
  g += drawShelves(ox + ts, L * sc, s.leftShelves);
  g += drawShelves(divX + ts, rightBay * sc, s.rightShelves);
  // doors
  if (s.doorType === "swing") {
    const dopts = [[ox + ts, L * sc], [divX + ts, rightBay * sc]];
    dopts.forEach(([x, w], i) => {
      g += `<rect x="${x}" y="${innerTop}" width="${w}" height="${innerH}" fill="${th.fill ? "rgba(57,198,255,0.14)" : "none"}" stroke="${th.accent}" stroke-width="2"/>`;
      const hx = i === 0 ? x + w - 12 : x + 12;
      g += `<circle cx="${hx}" cy="${innerTop + innerH / 2}" r="5" fill="${th.accent}"/>`;
    });
  } else if (s.doorType === "sliding") {
    const iw = dw - 2 * ts, w = iw * 0.55;
    g += `<rect x="${ox + ts}" y="${innerTop}" width="${w}" height="${innerH}" fill="none" stroke="${th.accent}" stroke-width="2"/>`;
    g += `<rect x="${ox + dw - ts - w}" y="${innerTop}" width="${w}" height="${innerH}" fill="none" stroke="${th.accent}" stroke-width="2" stroke-dasharray="6 4"/>`;
  }
  // dimension callouts
  g += arrowDim(ox, oy + dh + 40, ox + dw, oy + dh + 40, `${W} ${unit}`, th.sub, true);
  g += arrowDim(ox - 34, oy, ox - 34, oy + dh, `${H} ${unit}`, th.sub, false);
  g += arrowDim(ox + ts, oy - 22, divX, oy - 22, `${L} ${unit}`, th.accent, true);
  g += arrowDim(divX + ts, oy - 22, ox + dw - ts, oy - 22, `${rightBay} ${unit}`, th.accent, true);
  return g;
}

function sideElevation(px, py, pw, ph, s, th) {
  const { D, H, T, unit } = s;
  const sc = Math.min((pw - 170) / D, (ph - 200) / H);
  const dw = D * sc, dh = H * sc, ts = Math.max(2, T * sc);
  const ox = px + 90 + (pw - 170 - dw) / 2, oy = py + 56 + (ph - 160 - dh) / 2;
  let g = `<text x="${px + pw / 2}" y="${py + 30}" fill="${th.txt}" font-size="25" font-weight="700" text-anchor="middle" font-family="monospace">SIDE</text>`;
  g += `<rect x="${ox}" y="${oy}" width="${dw}" height="${dh}" fill="none" stroke="${th.line}" stroke-width="2"/>`;
  if (th.fill) {
    g += `<rect x="${ox}" y="${oy}" width="${dw}" height="${ts}" fill="${th.frame}"/>`;
    g += `<rect x="${ox}" y="${oy + dh - ts}" width="${dw}" height="${ts}" fill="${th.frame}"/>`;
  }
  g += arrowDim(ox, oy + dh + 40, ox + dw, oy + dh + 40, `${D} ${unit}`, th.sub, true);
  g += arrowDim(ox - 34, oy, ox - 34, oy + dh, `${H} ${unit}`, th.sub, false);
  return g;
}

function isometric(px, py, pw, ph, s, th) {
  const { W, H, D, T, L } = s;
  const A = Math.PI / 6;
  const raw = (x, y, z) => [(x - z) * Math.cos(A), (x + z) * Math.sin(A) - y];
  const cs = [];
  for (const x of [0, W]) for (const y of [0, H]) for (const z of [0, D]) cs.push(raw(x, y, z));
  const xs = cs.map((c) => c[0]), ys = cs.map((c) => c[1]);
  const minx = Math.min(...xs), maxx = Math.max(...xs), miny = Math.min(...ys), maxy = Math.max(...ys);
  const sc = Math.min((pw - 80) / (maxx - minx), (ph - 90) / (maxy - miny));
  const ox = px + (pw - (maxx - minx) * sc) / 2 - minx * sc;
  const oy = py + 50 + (ph - 50 - (maxy - miny) * sc) / 2 - miny * sc;
  const P = (x, y, z) => { const [a, b] = raw(x, y, z); return `${ox + a * sc},${oy + b * sc}`; };
  let g = `<text x="${px + pw / 2}" y="${py + 28}" fill="${th.txt}" font-size="25" font-weight="700" text-anchor="middle" font-family="monospace">ISOMETRIC</text>`;
  g += `<polygon points="${P(0, H, 0)} ${P(W, H, 0)} ${P(W, H, D)} ${P(0, H, D)}" fill="${th.frame}" stroke="${th.line}" stroke-width="2"/>`;
  g += `<polygon points="${P(0, 0, 0)} ${P(0, H, 0)} ${P(0, H, D)} ${P(0, 0, D)}" fill="#16406b" stroke="${th.line}" stroke-width="2"/>`;
  g += `<polygon points="${P(0, 0, 0)} ${P(W, 0, 0)} ${P(W, H, 0)} ${P(0, H, 0)}" fill="rgba(46,110,160,0.22)" stroke="${th.line}" stroke-width="2"/>`;
  g += `<line x1="${P(T + L, 0, 0).split(",")[0]}" y1="${P(T + L, 0, 0).split(",")[1]}" x2="${P(T + L, H, 0).split(",")[0]}" y2="${P(T + L, H, 0).split(",")[1]}" stroke="${th.accent}" stroke-width="2"/>`;
  return g;
}

function buildSvg(s, mode) {
  const th = THEMES[mode];
  const v = validate(s);
  let out = `<svg xmlns="http://www.w3.org/2000/svg" width="${SVGW}" height="${SVGH}" viewBox="0 0 ${SVGW} ${SVGH}" font-family="monospace">`;
  out += `<rect width="${SVGW}" height="${SVGH}" fill="${th.bg}"/>`;
  if (mode === "cad") {
    for (let x = 0; x <= SVGW; x += 40) out += `<line x1="${x}" y1="0" x2="${x}" y2="${SVGH}" stroke="${th.grid}" stroke-width="1"/>`;
    for (let y = 0; y <= SVGH; y += 40) out += `<line x1="0" y1="${y}" x2="${SVGW}" y2="${y}" stroke="${th.grid}" stroke-width="1"/>`;
  }
  out += `<rect x="24" y="22" width="${SVGW - 48}" height="82" fill="none" stroke="${th.accent}" stroke-width="2"/>`;
  out += `<text x="42" y="60" fill="${th.txt}" font-size="32" font-weight="800">VAN FLATPACK · TWIN-BAY CABINET</text>`;
  out += `<text x="42" y="90" fill="${th.sub}" font-size="21">${s.W}(W) × ${s.H}(H) × ${s.D}(D) ${s.unit} · board ${s.T} · L-bay ${s.L} · R-bay ${v.rightBay} · ${s.back === "recessed" ? "recessed back" : "square back"}</text>`;
  if (!v.ok) {
    out += `<text x="${SVGW / 2}" y="${SVGH / 2 - 20}" fill="#ff5566" font-size="34" font-weight="800" text-anchor="middle">⚠ INVALID GEOMETRY</text>`;
    out += `<text x="${SVGW / 2}" y="${SVGH / 2 + 30}" fill="${th.txt}" font-size="24" text-anchor="middle">${v.msg}</text>`;
    out += `</svg>`;
    return out;
  }
  out += frontElevation(24, 120, 620, 680, s, th);
  out += sideElevation(660, 120, 516, 680, s, th);
  if (th.iso) out += isometric(24, 820, 700, 340, s, th);
  // compact legend
  const { rows } = computePanels(s);
  const lx = th.iso ? 740 : 60, ly = th.iso ? 850 : 850;
  out += `<text x="${lx}" y="${ly}" fill="${th.accent}" font-size="24" font-weight="800">CUTTING LIST (${s.unit})</text>`;
  rows.forEach((r, i) => {
    const y = ly + 40 + i * 34;
    out += `<text x="${lx}" y="${y}" fill="${th.txt}" font-size="21">${r.q}× ${r.p}</text>`;
    out += `<text x="${lx + 360}" y="${y}" fill="${th.sub}" font-size="21">${r.size}</text>`;
  });
  out += `</svg>`;
  return out;
}

// ── VECTOR PDF (IKEA manual style, multi-page, no bitmaps) ────────────────
function buildPdf(s, name) {
  const v = validate(s);
  const { rows } = computePanels(s);
  const hw = computeHardware(s, rows);
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const PW = 210, M = 14;
  // PAGE 1 — title + legend + hardware
  doc.setFont("courier", "bold"); doc.setFontSize(20);
  doc.text("VAN FLATPACK — ASSEMBLY MANUAL", M, 20);
  doc.setFontSize(11); doc.setFont("courier", "normal");
  doc.text(`${s.W}(W) x ${s.H}(H) x ${s.D}(D) ${s.unit}  |  board ${s.T}  |  L-bay ${s.L}  |  R-bay ${v.rightBay}`, M, 28);
  doc.setDrawColor(0); doc.line(M, 32, PW - M, 32);
  // Cutting list table
  doc.setFont("courier", "bold"); doc.setFontSize(13); doc.text("CUTTING LIST LEGEND", M, 42);
  doc.setFontSize(10);
  doc.text("QTY", M, 50); doc.text("PART", M + 18, 50); doc.text(`SIZE (${s.unit})`, M + 110, 50); doc.text("NOTE", M + 150, 50);
  doc.setFont("courier", "normal");
  let y = 57;
  rows.forEach((r) => {
    doc.line(M, y - 4, PW - M, y - 4);
    doc.text(String(r.q), M, y); doc.text(r.p, M + 18, y); doc.text(r.size, M + 110, y); doc.text(r.note || "", M + 150, y);
    y += 8;
  });
  doc.line(M, y - 4, PW - M, y - 4);
  // Hardware matrix
  y += 10;
  doc.setFont("courier", "bold"); doc.setFontSize(13); doc.text("HARDWARE PACKAGE", M, y); y += 8;
  doc.setFontSize(10); doc.text("QTY", M, y); doc.text("ITEM", M + 18, y); doc.setFont("courier", "normal"); y += 3;
  hw.forEach((h) => { doc.line(M, y, PW - M, y); y += 5; doc.text(String(h.q), M, y); doc.text(h.p, M + 18, y); y += 3; });
  doc.line(M, y, PW - M, y);
  // PAGE 2 — vector assembly diagram
  doc.addPage();
  doc.setFont("courier", "bold"); doc.setFontSize(16); doc.text("STEP 1 — CARCASS ASSEMBLY", M, 20);
  doc.setLineWidth(0.5); doc.setDrawColor(0);
  const dx = M, dy = 34, aw = PW - 2 * M, ah = 180;
  const sc = Math.min((aw - 20) / s.W, (ah - 20) / s.H);
  const w = s.W * sc, h = s.H * sc, ts = Math.max(0.8, s.T * sc);
  const ox = dx + (aw - w) / 2, oy = dy + 10;
  const rect = (x, yy, ww, hh) => doc.rect(x, yy, ww, hh);
  rect(ox, oy, w, h);                         // outer
  rect(ox, oy, ts, h); rect(ox + w - ts, oy, ts, h); // sides
  rect(ox, oy, w, ts); rect(ox, oy + h - ts, w, ts); // top/bottom
  const divx = ox + (s.T + s.L) * sc; rect(divx, oy + ts, ts, h - 2 * ts); // divider
  const shelf = (x0, ww, n) => { for (let i = 1; i <= n; i++) { const yy = oy + ts + (h - 2 * ts) * i / (n + 1); doc.line(x0, yy, x0 + ww, yy); } };
  shelf(ox + ts, s.L * sc, s.leftShelves); shelf(divx + ts, v.rightBay * sc, s.rightShelves);
  // dimension callouts
  doc.setFontSize(9);
  doc.line(ox, oy + h + 8, ox + w, oy + h + 8);
  doc.text(`${s.W} ${s.unit}`, ox + w / 2 - 8, oy + h + 6);
  doc.text(`${s.H} ${s.unit}`, ox - 12, oy + h / 2);
  doc.text(`L ${s.L}`, ox + ts + 2, oy - 2); doc.text(`R ${v.rightBay}`, divx + ts + 2, oy - 2);
  doc.setFontSize(9); doc.setFont("courier", "normal");
  doc.text("Fix sides + centre divider between top & bottom using Confirmat screws / dowels, then square with the back panel.", M, oy + h + 22, { maxWidth: aw });
  doc.save(`${name || "van_cabinet_manual"}.pdf`);
}

// ── UI ───────────────────────────────────────────────────────────────────
const StepBtn = ({ onClick, children }) => (
  <button type="button" onClick={onClick} className="w-9 h-9 rounded-lg border-2 border-slate-600 bg-slate-800 text-cyan-300 flex items-center justify-center active:scale-95">{children}</button>
);
function Field({ label, value, set, step = 10, min = 1 }) {
  return (
    <div className="space-y-1">
      <label className="text-[10px] uppercase tracking-widest text-slate-400 font-mono">{label}</label>
      <div className="flex items-center gap-2">
        <StepBtn onClick={() => set(Math.max(min, num(value) - step))}><Minus size={15} /></StepBtn>
        <input type="number" value={value} onChange={(e) => set(e.target.value)}
          className="flex-1 min-w-0 bg-slate-900 border-2 border-slate-700 focus:border-cyan-500 outline-none px-2 py-2 text-center text-sm text-white font-mono rounded-lg" />
        <StepBtn onClick={() => set(num(value) + step)}><Plus size={15} /></StepBtn>
      </div>
    </div>
  );
}

export default function VanCabinetDesigner({ onExit }) {
  const [unit, setUnit] = useState("mm");
  const [mode, setMode] = useState("cad");
  const [W, setW] = useState(1000);
  const [H, setH] = useState(900);
  const [D, setD] = useState(400);
  const [T, setT] = useState(16);
  const [L, setL] = useState(450);
  const [back, setBack] = useState("square");
  const [leftShelves, setLeftShelves] = useState(1);
  const [rightShelves, setRightShelves] = useState(2);
  const [doorType, setDoorType] = useState("swing");
  const [busy, setBusy] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [resultUrl, setResultUrl] = useState(null);
  const [name, setName] = useState("van_cabinet_spec");

  const spec = useMemo(() => ({
    W: num(W, 1), H: num(H, 1), D: num(D, 1), T: num(T, 1), L: num(L, 1),
    back, leftShelves: Math.max(0, Math.min(8, num(leftShelves))), rightShelves: Math.max(0, Math.min(8, num(rightShelves))),
    doorType, unit,
  }), [W, H, D, T, L, back, leftShelves, rightShelves, doorType, unit]);

  const valid = useMemo(() => validate(spec), [spec]);
  const svg = useMemo(() => buildSvg(spec, mode), [spec, mode]);
  const svgSrc = useMemo(() => "data:image/svg+xml;charset=utf-8," + encodeURIComponent(svg), [svg]);

  const generate = async () => {
    if (!valid.ok) { toast.error(valid.msg); return; }
    setBusy(true);
    try {
      const img = new Image();
      await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = svgSrc; });
      const scale = 2, c = document.createElement("canvas");
      c.width = SVGW * scale; c.height = SVGH * scale;
      c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
      const blob = await new Promise((r) => c.toBlob(r, "image/png", 0.95));
      if (resultUrl) URL.revokeObjectURL(resultUrl);
      setResultUrl(URL.createObjectURL(blob));
    } catch (e) { toast.error("Could not render the blueprint"); }
    finally { setBusy(false); }
  };

  const savePdf = async () => {
    if (!valid.ok) { toast.error(valid.msg); return; }
    setPdfBusy(true);
    try { buildPdf(spec, name); toast.success("Printable PDF saved ✓"); }
    catch (e) { toast.error("Could not build the PDF"); }
    finally { setPdfBusy(false); }
  };

  if (resultUrl) {
    return (
      <div className="h-full w-full flex flex-col min-h-0 bg-slate-950 text-slate-100" data-testid="cad-result">
        <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-4 pt-4 pb-4 space-y-4">
          <p className="font-mono text-[11px] font-black uppercase tracking-widest text-cyan-300">Blueprint ready</p>
          <img src={resultUrl} alt="cabinet spec" data-testid="cad-result-image" className="w-full border-2 border-black rounded-md" />
          <input value={name} onChange={(e) => setName(e.target.value)} data-testid="cad-name"
            className="w-full bg-slate-900 border-2 border-slate-700 focus:border-cyan-500 outline-none px-3 py-2.5 text-sm text-white font-serif rounded-lg" />
          <button onClick={savePdf} disabled={pdfBusy} data-testid="cad-save-pdf"
            className="w-full flex items-center justify-center gap-2 border-2 border-black bg-[#FFD028] text-black py-3 rounded-lg font-mono text-[11px] font-black uppercase tracking-widest shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none disabled:opacity-60">
            {pdfBusy ? <Loader2 size={15} className="animate-spin" /> : <FileDown size={15} />} Printable PDF manual (A4)
          </button>
          <p className="text-center font-mono text-[9px] text-slate-500 -mt-1">…or save the image anywhere:</p>
          <SaveDestinations url={resultUrl} track="IMAGES" filename={name || "van_cabinet_spec"} ext="png" />
          <button onClick={() => { if (resultUrl) URL.revokeObjectURL(resultUrl); setResultUrl(null); }} data-testid="cad-back-edit"
            className="w-full flex items-center justify-center gap-2 border-2 border-slate-700 bg-slate-900 text-slate-200 py-2.5 rounded-lg font-mono text-[10px] font-black uppercase tracking-widest">
            <ArrowLeft size={14} /> Edit design
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full flex flex-col min-h-0 bg-slate-950 text-slate-100" data-testid="cad-designer">
      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-4 pt-4 pb-4 space-y-4">
        <div className="flex items-center justify-between">
          <p className="font-mono text-[11px] font-black uppercase tracking-widest text-cyan-300 flex items-center gap-2"><Ruler size={14} /> Van Flatpack CAD</p>
          {onExit && <button onClick={onExit} data-testid="cad-exit" className="text-[11px] font-mono text-slate-400 hover:text-white">✕ Close</button>}
        </div>

        <img src={svgSrc} alt="blueprint preview" data-testid="cad-preview" className="w-full border-2 border-black rounded-md" />

        {!valid.ok && (
          <div data-testid="cad-error" className="flex items-center gap-2 border-2 border-red-500/60 bg-red-500/10 text-red-300 px-3 py-2 rounded-lg font-mono text-[11px]">
            <AlertTriangle size={15} /> {valid.msg}
          </div>
        )}

        {/* View state */}
        <div className="grid grid-cols-2 gap-2">
          {[["cad", "CAD Blueprint"], ["ikea", "IKEA Line-Art"]].map(([k, lbl]) => (
            <button key={k} onClick={() => setMode(k)} data-testid={`cad-mode-${k}`}
              className={`py-2.5 rounded-lg border-2 font-mono text-[10px] font-black uppercase tracking-widest ${mode === k ? "border-cyan-400 bg-cyan-400/15 text-cyan-200" : "border-slate-700 bg-slate-900 text-slate-400"}`}>{lbl}</button>
          ))}
        </div>

        {/* Units */}
        <div className="grid grid-cols-3 gap-2">
          {["mm", "cm", "in"].map((u) => (
            <button key={u} onClick={() => setUnit(u)} data-testid={`cad-unit-${u}`}
              className={`py-2 rounded-lg border-2 font-mono text-[10px] font-black uppercase tracking-widest ${unit === u ? "border-cyan-400 bg-cyan-400/15 text-cyan-200" : "border-slate-700 bg-slate-900 text-slate-400"}`}>{u}</button>
          ))}
        </div>

        {/* Dimensions */}
        <div className="grid grid-cols-3 gap-2">
          <Field label={`Width (${unit})`} value={W} set={setW} />
          <Field label={`Height (${unit})`} value={H} set={setH} />
          <Field label={`Depth (${unit})`} value={D} set={setD} />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Field label={`Board thickness`} value={T} set={setT} step={1} />
          <Field label={`Left bay width`} value={L} set={setL} />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Field label={`Left shelves`} value={leftShelves} set={setLeftShelves} step={1} min={0} />
          <Field label={`Right shelves`} value={rightShelves} set={setRightShelves} step={1} min={0} />
        </div>

        {/* Back panel */}
        <div className="grid grid-cols-2 gap-2">
          {[["square", "Square back"], ["recessed", "Recessed back"]].map(([k, lbl]) => (
            <button key={k} onClick={() => setBack(k)} data-testid={`cad-back-${k}`}
              className={`py-2 rounded-lg border-2 font-mono text-[10px] font-black uppercase tracking-widest ${back === k ? "border-cyan-400 bg-cyan-400/15 text-cyan-200" : "border-slate-700 bg-slate-900 text-slate-400"}`}>{lbl}</button>
          ))}
        </div>

        {/* Doors */}
        <div>
          <label className="text-[10px] uppercase tracking-widest text-slate-400 font-mono">Doors (per bay)</label>
          <div className="grid grid-cols-3 gap-2 mt-1">
            {[["none", "None"], ["swing", "Swing"], ["sliding", "Sliding"]].map(([k, lbl]) => (
              <button key={k} onClick={() => setDoorType(k)} data-testid={`cad-door-${k}`}
                className={`py-2 rounded-lg border-2 font-mono text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-1.5 ${doorType === k ? "border-cyan-400 bg-cyan-400/15 text-cyan-200" : "border-slate-700 bg-slate-900 text-slate-400"}`}>
                {k === "swing" ? <DoorOpen size={13} /> : k === "sliding" ? <DoorClosed size={13} /> : null} {lbl}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 pt-1">
          <button onClick={() => { setW(1000); setH(900); setD(400); setT(16); setL(450); setBack("square"); setLeftShelves(1); setRightShelves(2); setDoorType("swing"); }} data-testid="cad-reset"
            className="flex items-center justify-center gap-2 border-2 border-slate-700 bg-slate-900 text-slate-300 py-3 rounded-lg font-mono text-[10px] font-black uppercase tracking-widest">
            <RotateCcw size={13} /> Reset
          </button>
          <button onClick={generate} disabled={busy || !valid.ok} data-testid="cad-generate"
            className="flex items-center justify-center gap-2 border-2 border-black bg-cyan-400 text-black py-3 rounded-lg font-mono text-[10px] font-black uppercase tracking-widest shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none disabled:opacity-50">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />} Blueprint
          </button>
        </div>
      </div>
    </div>
  );
}
